/* =========================================================================
   Product Zoom Modal — PDP Festina PE
   Modal a pantalla completa (estilo Festina):
   - Abre al hacer clic en cualquier imagen de la galería del producto.
   - Miniaturas verticales flotando sobre la imagen (con flechas arriba/abajo).
   - La imagen ocupa el 100% del ancho; NO hay zoom adicional.
   - Al mover el cursor verticalmente, la imagen se recorre en vertical
     (cursor arriba -> baja la imagen; cursor abajo -> sube la imagen).
   - En táctil se arrastra con el dedo para recorrerla.
   ========================================================================= */
(() => {
  'use strict';

  const ready = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  ready(() => {
    const modal = document.querySelector('[data-product-zoom-modal]');
    if (!modal) return;

    const stage    = modal.querySelector('[data-fz-stage]');
    const imgEl    = modal.querySelector('[data-fz-img]');
    const swiperEl = modal.querySelector('[data-fz-swiper]');
    const thumbs   = modal.querySelector('[data-fz-thumbs]');   // .swiper-wrapper
    const rail     = modal.querySelector('.fz-rail');
    const railPrev = modal.querySelector('[data-fz-rail-prev]');
    const railNext = modal.querySelector('[data-fz-rail-next]');
    const btnClose = modal.querySelector('[data-fz-close]');
    let   swiper   = null;

    // --- Fuente de datos: JSON emitido por Liquid o, si falta, la galería del DOM.
    let slides = readData();
    if (!slides.length) slides = readFromDom();
    if (!slides.length) return;

    // Estado
    let current = 0;
    let ty = 0;                         // desplazamiento vertical actual (px)
    let dragging = false, startY = 0, startTy = 0;
    // Medidas cacheadas (se recalculan al abrir / cambiar imagen / redimensionar)
    let overflow = 0, stageTop = 0, stageH = 1;
    let rafId = 0;

    // ------------------------------------------------------------------ datos
    function readData() {
      const node = document.querySelector('[data-product-zoom-data]');
      if (!node) return [];
      try {
        const arr = JSON.parse(node.textContent.trim());
        return (arr || []).filter((s) => s && s.full);
      } catch {
        return [];
      }
    }

    function readFromDom() {
      return [...document.querySelectorAll('[data-image-zoom-wrapper][data-zoom]')].map((w) => {
        const img = w.querySelector('img');
        return {
          full: w.getAttribute('data-zoom'),
          thumb: img?.currentSrc || img?.src || img?.getAttribute('data-src') || w.getAttribute('data-zoom'),
          alt: img?.getAttribute('alt') || '',
        };
      });
    }

    // ------------------------------------------------------------- galería PDP
    function bindGalleryClicks() {
      const group = document.querySelector('[data-product-single-media-group]') || document;
      group.addEventListener('click', (e) => {
        const target = e.target.closest(
          '[data-image-zoom], .product-single__media, .product-featured-media, #slider-imagenes-mobile img'
        );
        if (!target) return;
        // No robar el clic a videos / model-viewer.
        if (e.target.closest('[data-product-media-type-video], [data-product-media-type-model], video, model-viewer')) return;

        e.preventDefault();
        open(indexFromEvent(target));
      });
    }

    // Determina qué imagen de la galería se pulsó (por orden en el DOM).
    function indexFromEvent(target) {
      const wrapper = target.closest('[data-product-single-media-wrapper], .swiper-slide, li');
      const all = [...document.querySelectorAll('[data-product-single-media-wrapper]')];
      const idx = wrapper ? all.indexOf(wrapper) : -1;
      if (idx >= 0 && idx < slides.length) return idx;

      // Alternativa: emparejar por nombre de archivo.
      const img = target.tagName === 'IMG' ? target : target.querySelector('img');
      const src = img?.currentSrc || img?.src || img?.getAttribute('data-src');
      if (src) {
        const base = fileKey(src);
        const found = slides.findIndex((s) => fileKey(s.thumb) === base || fileKey(s.full) === base);
        if (found >= 0) return found;
      }
      return 0;
    }

    const fileKey = (url) =>
      url ? url.split('?')[0].split('/').pop().replace(/_\d+x\d*(@\dx)?\./, '.') : '';

    // ------------------------------------------------------------- miniaturas
    function buildThumbs() {
      if (!thumbs) return;
      thumbs.innerHTML = '';
      if (slides.length < 2) {
        if (rail) rail.style.display = 'none';   // una sola imagen: sin riel
        return;
      }
      slides.forEach((s, i) => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide fz-thumb';
        slide.setAttribute('role', 'button');
        slide.setAttribute('aria-label', `Imagen ${i + 1}`);

        const img = document.createElement('img');
        img.src = s.thumb || s.full;
        img.alt = s.alt || '';
        img.loading = 'lazy';

        slide.append(img);
        slide.addEventListener('click', (ev) => {
          ev.stopPropagation();
          goTo(i);
        });
        thumbs.append(slide);
      });
      initSwiper();
    }

    // Carrusel vertical de miniaturas: 4 por vista (horizontal en móvil).
    function initSwiper() {
      if (!swiperEl || typeof window.Swiper !== 'function' || swiper) return;
      swiper = new window.Swiper(swiperEl, {
        direction: 'vertical',
        slidesPerView: 4,
        spaceBetween: 12,
        watchOverflow: true,          // oculta las flechas si caben todas
        watchSlidesProgress: true,    // habilita visibleSlidesIndexes
        navigation: { prevEl: railPrev, nextEl: railNext },
        breakpoints: {
          0:   { direction: 'horizontal', slidesPerView: 4, spaceBetween: 8 },
          750: { direction: 'vertical',   slidesPerView: 4, spaceBetween: 12 },
        },
      });
    }

    function syncThumbs() {
      if (!thumbs) return;
      thumbs.querySelectorAll('.fz-thumb').forEach((t, i) => {
        t.classList.toggle('selected-item', i === current);
      });
      // Si la miniatura activa no está a la vista, desplaza el carrusel.
      if (swiper) {
        const visible = swiper.visibleSlidesIndexes || [];
        if (!visible.includes(current)) swiper.slideTo(current);
      }
    }

    // -------------------------------------------------------- abrir / cerrar
    function open(index) {
      current = clampIndex(index);
      show(current, true);
      modal.classList.add('fz-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('fz-lock');
      // El modal estaba oculto: Swiper debe recalcular medidas ahora que es visible.
      swiper?.update();
      syncThumbs();
    }

    function close() {
      modal.classList.remove('fz-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fz-lock');
      resetView();
    }

    function show(index, animate) {
      const s = slides[index];
      resetView();
      imgEl.classList.remove('fz-anim');
      imgEl.src = s.full;
      imgEl.alt = s.alt || '';
      if (animate) {
        void imgEl.offsetWidth; // reinicia la animación de entrada (reflow)
        imgEl.classList.add('fz-anim');
      }
      // Si la imagen ya está en caché, mide de inmediato; si no, lo hará al cargar.
      if (imgEl.complete && imgEl.naturalHeight) measure();
    }

    function goTo(index) {
      current = clampIndex(index);
      show(current, false); // sin animación al cambiar por miniatura/flechas
      syncThumbs();
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }
    // circular
    function clampIndex(i) { return ((i % slides.length) + slides.length) % slides.length; }

    // ------------------------------------------------- desplazamiento vertical
    // Aplica el transform en el próximo frame (evita tirones por reflow).
    const scheduleApply = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        imgEl.style.transform = `translateY(${ty}px)`;
      });
    };

    // Recalcula medidas: cuánto sobresale la imagen y la geometría del escenario.
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      stageTop = rect.top;
      stageH = rect.height || 1;
      overflow = Math.max(0, imgEl.offsetHeight - stage.clientHeight);
    };

    const resetView = () => {
      ty = 0;
      imgEl.style.transform = '';
    };

    // Recorre la imagen según la posición vertical del cursor dentro del escenario.
    // ratio 0 (arriba)  -> muestra la parte superior (imagen desplazada hacia abajo)
    // ratio 1 (abajo)   -> muestra la parte inferior (imagen desplazada hacia arriba)
    const followCursor = (clientY) => {
      if (overflow === 0) return;
      const ratio = Math.min(1, Math.max(0, (clientY - stageTop) / stageH));
      ty = overflow / 2 - ratio * overflow;
      scheduleApply();
    };

    const dragTo = (clientY) => {
      const half = overflow / 2;
      ty = Math.min(half, Math.max(-half, startTy + (clientY - startY)));
      scheduleApply();
    };

    // ------------------------------------------------------- eventos de gestos
    function bindGestures() {
      // Desktop: seguir el cursor. Al salir, la imagen se queda donde quedó.
      stage.addEventListener('mousemove', (e) => followCursor(e.clientY));

      // Táctil: arrastrar con el dedo.
      stage.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          dragging = true;
          startY = e.touches[0].clientY;
          startTy = ty;
          imgEl.style.transition = 'none';
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchmove',
        (e) => {
          if (!dragging || e.touches.length !== 1) return;
          if (overflow > 0) e.preventDefault();
          dragTo(e.touches[0].clientY);
        },
        { passive: false }
      );
      stage.addEventListener('touchend', (e) => {
        if (e.touches.length === 0) {
          dragging = false;
          imgEl.style.transition = '';
        }
      });

      // Recalcula medidas cuando la imagen termina de cargar.
      imgEl.addEventListener('load', measure);
      imgEl.addEventListener('dragstart', (e) => e.preventDefault());
    }

    // ---------------------------------------------------------- controles UI
    function bindControls() {
      bindGestures();

      btnClose?.addEventListener('click', (e) => {
        e.stopPropagation();
        close();
      });
      // Las flechas del riel las controla Swiper (navigation).

      document.addEventListener('keydown', (e) => {
        if (!modal.classList.contains('fz-open')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
      });

      window.addEventListener('resize', () => measure());
    }

    // ------------------------------------------------------------ arranque
    // Se ejecuta al final: todo (funciones y const) ya está definido.
    buildThumbs();
    bindGalleryClicks();
    bindControls();
  });
})();
